/**
 * User Preferences Service
 *
 * Handles fetching user preferences from DB on fresh login.
 * Uses localStorage for tenant/instance persistence.
 *
 * Flow:
 * - Fresh login (no localStorage): Fetch from DB API → save to localStorage
 * - Header change: Update localStorage only (no DB)
 * - Settings page change: Update DB + localStorage
 * - Logout: Clear localStorage → next login fetches from DB
 */

import { getAuthorizationHeader, authService } from "../utils/authService";
import { logger } from "./logger.service";

export interface UserPreferences {
  userId: string;
  tenantId: string | null;
  instanceId: string | null;
  theme: "Light" | "Dark";
  language: "English" | "Spanish" | "French" | "German";
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Check if localStorage has tenant/instance data
 */
function hasLocalStorageData(): boolean {
  const instanceId = authService.getSavedInstanceId();
  const tenantId = authService.getSavedTenantId();
  return !!(instanceId || tenantId);
}

/**
 * Save all preferences to localStorage
 */
function saveAllPreferencesToLocalStorage(preferences: UserPreferences): void {
  if (preferences.tenantId) {
    authService.saveTenantId(preferences.tenantId);
  }
  if (preferences.instanceId) {
    authService.saveInstanceId(preferences.instanceId);
  }
  if (preferences.theme) {
    authService.saveTheme(preferences.theme);
  }
  if (preferences.language) {
    authService.saveLanguage(preferences.language);
  }
}

/**
 * Get all preferences from localStorage
 */
function getAllPreferencesFromLocalStorage(): UserPreferences {
  return {
    userId: "",
    tenantId: authService.getSavedTenantId() || null,
    instanceId: authService.getSavedInstanceId() || null,
    theme: authService.getSavedTheme() as "Light" | "Dark",
    language: authService.getSavedLanguage() as "English" | "Spanish" | "French" | "German",
    createdAt: null,
    updatedAt: null,
  };
}

/**
 * Fetch user preferences from API
 */
async function fetchPreferencesFromAPI(): Promise<UserPreferences | null> {
  try {
    const headers = await getAuthorizationHeader();
    const response = await fetch("/api/v1/user-preferences", {
      method: "GET",
      headers,
      cache: "no-store", // Prevent 304 responses - always get fresh data
    });

    if (!response.ok) {
      logger.error("Failed to fetch user preferences", undefined, { status: response.status });
      return null;
    }

    const data = await response.json();
    return data as UserPreferences;
  } catch (error) {
    logger.error("Error fetching user preferences", error);
    return null;
  }
}

/**
 * Save user preferences to API (for Settings page - persists to DB)
 * Also updates localStorage with the saved values on success
 */
export async function saveUserPreferencesToDB(
  preferences: Partial<Pick<UserPreferences, "tenantId" | "instanceId" | "theme" | "language">>,
): Promise<UserPreferences | null> {
  try {
    const authHeaders = await getAuthorizationHeader();
    const response = await fetch("/api/v1/user-preferences", {
      method: "PUT",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(preferences),
    });

    if (!response.ok) {
      logger.error("Failed to save user preferences", undefined, { status: response.status });
      return null;
    }

    const data = await response.json();
    const savedPreferences = data.preferences as UserPreferences;

    // Update localStorage with the saved values
    if (savedPreferences) {
      saveAllPreferencesToLocalStorage(savedPreferences);
    }

    return savedPreferences;
  } catch (error) {
    logger.error("Error saving user preferences", error);
    return null;
  }
}

/**
 * Load user preferences on app startup
 * - Fresh login (empty localStorage): Fetch from DB, save to localStorage
 * - Active session (localStorage has data): Use localStorage (user may have switched tenant via header)
 *
 * @returns User preferences or null
 */
export async function loadUserPreferences(): Promise<UserPreferences | null> {
  // Active session: localStorage has data, use it (respects header tenant changes)
  if (hasLocalStorageData()) {
    return getAllPreferencesFromLocalStorage();
  }

  // Fresh login: localStorage is empty, fetch from DB
  const preferences = await fetchPreferencesFromAPI();

  if (preferences) {
    // Save DB preferences to localStorage for subsequent use
    saveAllPreferencesToLocalStorage(preferences);
    return preferences;
  }

  return null;
}
